import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { ApiService, apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api.config';

interface AuthVerifyResponse {
    user: User;
    token: string;
    refreshToken: string;
    sessionId: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TokenManager = {
    setTokens: (accessToken: string, refreshToken: string) => {
        localStorage.setItem('auth0_access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
    },
    getAccessToken: () => localStorage.getItem('auth0_access_token'),
    clearTokens: () => {
        localStorage.removeItem('auth0_access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
    }
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();
    const {
        isAuthenticated: isAuth0Authenticated,
        user: auth0User,
        isLoading: auth0Loading,
        getAccessTokenSilently,
        logout: auth0Logout,
        loginWithRedirect
    } = useAuth0();

    const [user, setUser] = useState<User | null>(() => {
        try {
            const storedUser = localStorage.getItem('user');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error('Failed to parse stored user:', error);
            return null;
        }
    });

    const [isLoading, setIsLoading] = useState(true);
    const initializationRef = useRef(false);
    const tokenRefreshTimeout = useRef<NodeJS.Timeout>();

    const logAuthEvent = useCallback((event: string, data?: any) => {
        console.log(`Auth Event: ${event}`, {
            timestamp: new Date().toISOString(),
            userId: user?.id,
            ...data
        });
    }, [user]);

    const clearAuthState = useCallback(() => {
        logAuthEvent('clearing_auth_state');
        TokenManager.clearTokens();
        localStorage.removeItem('user');
        setUser(null);

        if (tokenRefreshTimeout.current) {
            clearTimeout(tokenRefreshTimeout.current);
            tokenRefreshTimeout.current = undefined;
        }
    }, [logAuthEvent]);

    const handleAuthError = useCallback((error: any) => {
        logAuthEvent('auth_error', { error });
        clearAuthState();
        navigate('/login', { replace: true });
    }, [clearAuthState, navigate, logAuthEvent]);

    const setupTokenRefresh = useCallback(async () => {
        try {
            const auth0Token = await getAccessTokenSilently({
                detailedResponse: true
            });

            if (!auth0Token.access_token) {
                throw new Error('Failed to get access token');
            }

            logAuthEvent('refreshing_token');

            const response = await apiService.post<AuthVerifyResponse>(
                API_ENDPOINTS.AUTH.AUTH0.VERIFY_TOKEN,
                { token: auth0Token.access_token }
            );

            if (response?.user) {
                TokenManager.setTokens(auth0Token.access_token, response.refreshToken);
                localStorage.setItem('user', JSON.stringify(response.user));
                setUser(response.user);

                // Calculate next refresh time (5 minutes before expiry)
                const expiresIn = auth0Token.expires_in ?? 3600; // Default to 1 hour if not specified
                const refreshDelay = (expiresIn - 300) * 1000; // Convert to milliseconds, refresh 5 mins early

                // Schedule next refresh
                if (tokenRefreshTimeout.current) {
                    clearTimeout(tokenRefreshTimeout.current);
                }

                tokenRefreshTimeout.current = setTimeout(() => {
                    setupTokenRefresh();
                }, refreshDelay);

                logAuthEvent('token_refresh_scheduled', { nextRefreshIn: refreshDelay });
            }
        } catch (error) {
            logAuthEvent('token_refresh_error', { error });
            handleAuthError(error);
        }
    }, [getAccessTokenSilently, handleAuthError, logAuthEvent]);

    useEffect(() => {
        if (auth0Loading || initializationRef.current) return;

        const initializeAuth = async () => {
            if (!isAuth0Authenticated || !auth0User?.email) {
                if (tokenRefreshTimeout.current) {
                    clearTimeout(tokenRefreshTimeout.current);
                }
                clearAuthState();
                setIsLoading(false);
                initializationRef.current = true;
                return;
            }

            try {
                setIsLoading(true);
                ApiService.setTokenGetter(getAccessTokenSilently);
                await setupTokenRefresh();
                initializationRef.current = true;
            } catch (error) {
                console.error('Auth initialization error:', error);
                handleAuthError(error);
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();

        return () => {
            if (tokenRefreshTimeout.current) {
                clearTimeout(tokenRefreshTimeout.current);
            }
        };
    }, [
        isAuth0Authenticated,
        auth0User,
        auth0Loading,
        getAccessTokenSilently,
        clearAuthState,
        setupTokenRefresh,
        handleAuthError
    ]);

    // Synchronize auth states
    useEffect(() => {
        if (!isAuth0Authenticated && user) {
            logAuthEvent('auth_state_mismatch');
            clearAuthState();
        }
    }, [isAuth0Authenticated, user, clearAuthState, logAuthEvent]);

    const handleLogout = useCallback(() => {
        logAuthEvent('logout_initiated');
        clearAuthState();
        auth0Logout({
            logoutParams: {
                returnTo: window.location.origin
            }
        });
    }, [auth0Logout, clearAuthState, logAuthEvent]);

    const value = {
        user,
        isLoading: isLoading || auth0Loading,
        isAuthenticated: !!user && isAuth0Authenticated,
        login: loginWithRedirect,
        logout: handleLogout
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};